import React, {
    useCallback,
    useEffect,
    useState,
    useRef,
    KeyboardEvent,
} from 'react';
import useDebounce from 'utils/hooks/useDebounce';
import { TextInput } from '../Text/TextInput';
import { Spinner } from 'shared/Spinner';
import { BaseInputProps } from 'shared/Input/types';
import classNames from 'classnames';

export const DEBOUNCE_TIME = 300; // milliseconds

type TypeaheadProps<T> = BaseInputProps & {
    onApply: (item: T) => Promise<void>;
    handleSuggestion: (query: string) => Promise<T[]>;
    renderSuggestion: (item: T) => React.ReactNode;
};

export const Typeahead = <T,>({
    value,
    disabled,
    onChange,
    onBlur,
    onApply,
    handleSuggestion,
    renderSuggestion,
    ...props
}: TypeaheadProps<T>) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [suggestions, setSuggestions] = useState<T[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    const [typeaheadValue, setTypeaheadValue] = useState<string>();
    const debouncedValue = useDebounce(typeaheadValue, DEBOUNCE_TIME);

    const fetch = useCallback(
        async (query: string) => {
            setIsLoading(true);
            try {
                const results = await handleSuggestion(query);
                setSuggestions(results);
                setSelectedIndex(0);
                setIsOpen(results.length > 0);
                setIsLoading(false);
            } catch (error) {
                setSuggestions([]);
                setIsOpen(false);
                setIsLoading(false);
            }
        },
        [handleSuggestion]
    );

    const applySuggestion = useCallback(
        async (item: T) => {
            setIsApplying(true);
            await onApply(item);
            setIsOpen(false);
            setIsApplying(false);
        },
        [onApply]
    );

    const handleKeydown = useCallback(
        (event: KeyboardEvent) => {
            const { key } = event;
            if (key === 'ArrowDown') {
                setSelectedIndex((i) => Math.min(suggestions.length - 1, ++i));
                event.preventDefault();
            } else if (key === 'ArrowUp') {
                setSelectedIndex((i) => Math.max(--i, 0));
                event.preventDefault();
            } else if (key === 'Enter') {
                const suggestion = suggestions[selectedIndex];
                if (suggestion) applySuggestion(suggestion);

                event.preventDefault();
            } else if (key === 'Escape') {
                inputRef.current?.blur();
            }
        },
        [applySuggestion, selectedIndex, suggestions]
    );

    useEffect(() => {
        if (debouncedValue) {
            fetch(debouncedValue);
        } else {
            setSuggestions([]);
            setIsOpen(false);
            setSelectedIndex(0);
        }
    }, [debouncedValue, fetch]);

    return (
        <div className="relative">
            <TextInput
                ref={inputRef}
                value={value}
                disabled={isApplying || disabled}
                onChange={(v) => {
                    onChange?.(v);
                    setTypeaheadValue(v);
                }}
                onBlur={(event) => {
                    onBlur?.(event);

                    const relatedTarget = event.relatedTarget;
                    if (!dropdownRef.current?.contains(relatedTarget)) {
                        setIsOpen(false);
                    }
                }}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={isOpen}
                aria-controls="typeahead-listbox"
                onKeyDown={handleKeydown}
                {...props}
            />

            {isLoading && (
                <div className="absolute right-3 top-2.5">
                    <Spinner size="sm" />
                </div>
            )}

            {isOpen && suggestions.length > 0 && (
                <div
                    ref={dropdownRef}
                    id="typeahead-listbox"
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                    tabIndex={-1}
                    role="listbox"
                >
                    <ul className="max-h-60 overflow-y-auto">
                        {suggestions.map((suggestion, index) => (
                            <li key={index} className="relative">
                                <button
                                    tabIndex={-1}
                                    className={classNames(
                                        'flex w-full items-center justify-between gap-2 text-sm py-1.5 px-2 hover:bg-gray-100',
                                        {
                                            'bg-gray-100':
                                                selectedIndex === index,
                                        }
                                    )}
                                    disabled={isApplying}
                                    onClick={() => applySuggestion(suggestion)}
                                >
                                    {renderSuggestion(suggestion)}
                                    {isApplying && selectedIndex === index && (
                                        <div className="absolute right-3 top-2.5">
                                            <Spinner size="sm" />
                                        </div>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
